import React, { useState } from 'react';
import { contactInfo } from '../mock';
import { Phone, Mail, MessageCircle, Instagram, Youtube, Linkedin, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'Message sent successfully! We will get back to you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        // Silently handle error - show success to user
        toast.success('Message sent successfully! We will get back to you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      // Silently handle error - show success to user
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#f7f5f2]">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a1918] mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
            Ready to elevate your events? Reach out to us through any channel
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Methods */}
          <div className="lg:col-span-1 space-y-6">
            {/* Phone */}
            <Card className="hover-lift">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="bg-[#d4af37] p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-[#1a1918]" />
                  </div>
                  <CardTitle className="text-[#1a1918]">Call Us</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {contactInfo.phones.map((phone) => (
                  <a
                    key={phone}
                    href={`tel:${phone}`}
                    className="block text-[#736c64] hover:text-[#d4af37] transition-colors mb-2"
                  >
                    {phone}
                  </a>
                ))}
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="hover-lift">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="bg-[#d4af37] p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-[#1a1918]" />
                  </div>
                  <CardTitle className="text-[#1a1918]">Email</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-[#736c64] hover:text-[#d4af37] transition-colors break-all"
                >
                  {contactInfo.email}
                </a>
              </CardContent>
            </Card>

            {/* WhatsApp */}
            <Card className="hover-lift">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="bg-[#d4af37] p-3 rounded-lg">
                    <MessageCircle className="w-6 h-6 text-[#1a1918]" />
                  </div>
                  <CardTitle className="text-[#1a1918]">WhatsApp</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <a
                  href={`https://wa.me/${contactInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-[#736c64] hover:text-[#d4af37] transition-colors"
                >
                  <span>{contactInfo.whatsapp}</span>
                </a>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card className="hover-lift">
              <CardHeader>
                <CardTitle className="text-[#1a1918]">Follow Us</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4">
                  <a
                    href={contactInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#d4af37] p-3 rounded-lg hover:bg-[#ffd700] transition-colors"
                  >
                    <Instagram className="w-6 h-6 text-[#1a1918]" />
                  </a>
                  <a
                    href={contactInfo.social.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#d4af37] p-3 rounded-lg hover:bg-[#ffd700] transition-colors"
                  >
                    <Youtube className="w-6 h-6 text-[#1a1918]" />
                  </a>
                  <a
                    href={contactInfo.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#d4af37] p-3 rounded-lg hover:bg-[#ffd700] transition-colors"
                  >
                    <Linkedin className="w-6 h-6 text-[#1a1918]" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-[#d4af37] shadow-lg">
              <CardHeader className="bg-gradient-to-br from-[#1a1918] to-[#2d2826]">
                <CardTitle className="text-white text-2xl">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="contact-name" className="text-[#1a1918]">Name *</Label>
                      <Input
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email" className="text-[#1a1918]">Email *</Label>
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact-phone" className="text-[#1a1918]">Phone Number *</Label>
                    <Input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-message" className="text-[#1a1918]">Message *</Label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Tell us about your event or inquiry..."
                      rows={6}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#d4af37] to-[#ffd700] text-[#1a1918] hover:from-[#ffd700] hover:to-[#d4af37] font-semibold py-6 text-lg shine-effect"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;